import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Search, Loader2 } from "lucide-react";
import { type PdfExtractionDocument } from "@/redux/api/documents.rtkq";
import { DataTable } from "./data-table";
import { createColumns } from "./columns";

interface PdfDocumentsTableProps {
  documents: PdfExtractionDocument[];
  isLoading: boolean;
  error?: unknown;
  onRefresh: () => void;
  onEdit: (doc: PdfExtractionDocument) => void;
  onDelete: (doc: PdfExtractionDocument) => void;
}

const PdfDocumentsTable: React.FC<PdfDocumentsTableProps> = ({ documents, isLoading, error, onRefresh, onEdit, onDelete }) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter documents by search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) {
      return documents;
    }
    const query = searchQuery.toLowerCase();
    return documents.filter((doc) => doc.storageFileName.toLowerCase().includes(query));
  }, [documents, searchQuery]);

  // Create columns with action callbacks
  const columns = useMemo(
    () =>
      createColumns({
        onEdit,
        onDelete,
      }),
    [onEdit, onDelete]
  );

  return (
    <Card className="w-full bg-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-chen-900">
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            חילוצי PDF ({filteredDocuments.length})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="חפש לפי שם הקובץ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pr-10 bg-white"
          />
        </div>

        {/* Table or Loading/Error States */}
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-slate-400">
            <Loader2 className="h-8 w-8 animate-spin mr-2" />
            טוען מסמכים...
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-600 mb-2">שגיאה בטעינת המסמכים</div>
            <div className="text-sm text-slate-400">
              {error && typeof error === "object" && "message" in error ? String(error.message) : "שגיאה לא ידועה"}
            </div>
            <Button variant="outline" size="sm" onClick={onRefresh} className="mt-4">
              נסה שוב
            </Button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchQuery ? "לא נמצאו מסמכים התואמים את החיפוש" : "אין חילוצי PDF עדיין. העלה קובץ PDF ראשון!"}
          </div>
        ) : (
          <DataTable columns={columns} data={filteredDocuments} />
        )}
      </CardContent>
    </Card>
  );
};

export default PdfDocumentsTable;
